import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import BackLink from '../../BackLink';
import EntityTitle from '../../EntityTitle';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import * as providerSlice from '../../../data/provider.slice';
import * as vehicleSlice from '../../../data/vehicle.slice';
import VehicleDialog from './VehicleDialog';
import BasicInfo from './BasicInfo';
import TabPanel from '../../TabPanel';
import Attributes from './Attributes';
import VehicleDocuments from './VehicleDocuments';
import Entity, { entityClasses } from '../Entity';
import { useCanEditVehicle } from '../../../hooks/permissions/useCanEditVehicle';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';

export default function Vehicle() {
  const { t } = useTranslation('common');
  const canEditVehicle = useCanEditVehicle();
  const isProviderUser = !!useLoggedInProviderId();

  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicleTab, setVehicleTab] = React.useState(0);
  const [editorOpen, setEditorOpen] = React.useState(false);

  const loading = useAppSelector(vehicleSlice.selectLoadingVehicle);
  const vehicle = useAppSelector(vehicleSlice.selectVehicle);
  const uploadingDocument = useAppSelector(
    vehicleSlice.selectUploadingDocument,
  );
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const id = parseInt(params.id!, 10);
    dispatch(vehicleSlice.getVehicleAsync(id));
  }, [params, dispatch]);

  const provider = useAppSelector(providerSlice.selectProvider);
  React.useEffect(() => {
    if (vehicle?.transportationProviderID) {
      dispatch(
        providerSlice.getProviderAsync(vehicle.transportationProviderID),
      );
    }
  }, [vehicle, dispatch]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setVehicleTab(newValue);
  };

  const editVehicle = () => {
    setEditorOpen(true);
  };

  const closeVehicleEditor = () => {
    setEditorOpen(false);
    dispatch(vehicleSlice.resetSavingFlags());
  };

  return (
    <Entity>
      <BackLink />
      <Box component="div" className={entityClasses.content}>
        <Box component="div" className={entityClasses.left}>
          <Card className={entityClasses.card}>
            <CardHeader
              title={
                <EntityTitle
                  icon={<AirportShuttleIcon />}
                  label={`${vehicle?.vehicleYear} ${vehicle?.vehicleMake}`}
                  loading={loading}
                  readOnly={!canEditVehicle}
                  onEdit={editVehicle}
                />
              }
            />
            {!loading && (
              <CardContent>
                <BasicInfo
                  titleStyle={entityClasses.section}
                  vehicle={vehicle}
                />
              </CardContent>
            )}
          </Card>
        </Box>
        {!loading && (
          <Box component="div" className={entityClasses.right}>
            <Paper className={entityClasses.tabs}>
              <Tabs
                indicatorColor="primary"
                textColor="primary"
                value={vehicleTab}
                onChange={handleTabChange}
                variant="fullWidth">
                <Tab label={t('vehicle.info')} />
                <Tab label={t('common.documents')} />
              </Tabs>
              <TabPanel value={vehicleTab} index={0}>
                <Attributes vehicle={vehicle} />
              </TabPanel>
              <TabPanel value={vehicleTab} index={1}>
                <VehicleDocuments
                  vehicle={vehicle}
                  uploadingDocument={uploadingDocument}
                  readOnly={!canEditVehicle}
                  allowView={!isProviderUser}
                />
              </TabPanel>
            </Paper>
          </Box>
        )}
      </Box>
      {vehicle?.transportationProviderID ? (
        <VehicleDialog
          title={t('vehicles.edit')}
          open={editorOpen}
          vehicle={vehicle}
          provider={provider!}
          close={closeVehicleEditor}
        />
      ) : null}
    </Entity>
  );
}
